import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { interviewsAPI } from '../api/client';

export default function CandidateScheduling() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [booked, setBooked] = useState(false);
  const [bookedSlot, setBookedSlot] = useState(null);

  useEffect(() => {
    interviewsAPI.getSchedule(token)
      .then((res) => {
        setData(res.data);
        if (res.data.already_booked) {
          setBooked(true);
          setBookedSlot(res.data.booked_slot);
        }
      })
      .catch((err) => setError(err.response?.data?.detail || 'Scheduling link not found or expired'))
      .finally(() => setLoading(false));
  }, [token]);

  // Derive sorted unique dates from slots
  const availableDates = useMemo(() => {
    if (!data) return [];
    const dates = [...new Set(data.slots.map((s) => s.slot_date))].sort();
    return dates;
  }, [data]);

  // Slots for the selected date
  const slotsForDate = useMemo(() => {
    if (!data || !selectedDate) return [];
    return data.slots.filter((s) => s.slot_date === selectedDate);
  }, [data, selectedDate]);

  const handleBook = async () => {
    if (!selectedSlot) return;
    setConfirming(true);
    try {
      await interviewsAPI.bookSlot(token, { slot_id: selectedSlot.id });
      setBooked(true);
      setBookedSlot(selectedSlot);
    } catch (err) {
      alert(err.response?.data?.detail || 'Booking failed. The slot may have just become unavailable. Please choose another.');
      // Reload to get fresh slot availability
      try {
        const res = await interviewsAPI.getSchedule(token);
        setData(res.data);
        setSelectedSlot(null);
      } catch (_) {}
    } finally {
      setConfirming(false);
    }
  };

  // ── Loading / error / already booked / success ──────────────────────────

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center p-8">
        <p className="text-5xl mb-4">❌</p>
        <h1 className="text-xl font-bold text-gray-800 mb-2">Link Unavailable</h1>
        <p className="text-gray-500">{error}</p>
      </div>
    </div>
  );

  if (booked && bookedSlot) return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-emerald-50">
      <div className="text-center p-8 max-w-md">
        <p className="text-6xl mb-4">🎉</p>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Interview Confirmed!</h1>
        <p className="text-gray-600 mb-6">
          Hi <strong>{data?.candidate_name}</strong>, your interview has been booked.
        </p>
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 text-left space-y-3 mb-6">
          <div className="flex items-center gap-3">
            <span className="text-2xl">📅</span>
            <div>
              <div className="text-xs text-gray-400 font-medium uppercase tracking-wide">Date</div>
              <div className="font-semibold text-gray-800">{fmtDateFull(bookedSlot.slot_date)}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-2xl">🕐</span>
            <div>
              <div className="text-xs text-gray-400 font-medium uppercase tracking-wide">Time</div>
              <div className="font-semibold text-gray-800">{fmtTime(bookedSlot.slot_time)}</div>
            </div>
          </div>
          {bookedSlot.location && (
            <div className="flex items-center gap-3">
              <span className="text-2xl">📍</span>
              <div>
                <div className="text-xs text-gray-400 font-medium uppercase tracking-wide">Location</div>
                <div className="font-semibold text-gray-800">{bookedSlot.location}</div>
              </div>
            </div>
          )}
        </div>
        <p className="text-sm text-gray-400">
          A confirmation email has been sent to you. We look forward to meeting you!
        </p>
      </div>
    </div>
  );

  // ── No slots available ──────────────────────────────────────────────────

  if (data && data.slots.length === 0) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center p-8 max-w-md">
        <p className="text-5xl mb-4">📅</p>
        <h1 className="text-xl font-bold text-gray-800 mb-2">No Slots Available</h1>
        <p className="text-gray-500">
          There are currently no available interview slots for your application.
          HR will be in touch with alternative arrangements.
        </p>
      </div>
    </div>
  );

  // ── Main scheduling UI ──────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-indigo-50 py-10 px-4">
      <div className="max-w-2xl mx-auto">

        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-primary-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-white font-bold text-xl">AI</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Schedule Your Interview</h1>
          <p className="text-gray-500 mt-1">
            Hi <strong>{data.candidate_name}</strong>
            {data.position_title && <> — for <strong>{data.position_title}</strong></>}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-6">

          {/* Step 1: Pick a date */}
          <div>
            <h2 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <span className="w-6 h-6 bg-primary-600 text-white rounded-full text-xs flex items-center justify-center font-bold">1</span>
              Choose a date
            </h2>
            <div className="flex flex-wrap gap-2">
              {availableDates.map((d) => {
                const hasAvailable = data.slots.some((s) => s.slot_date === d && s.available);
                return (
                  <button
                    key={d}
                    onClick={() => { setSelectedDate(d); setSelectedSlot(null); }}
                    disabled={!hasAvailable}
                    className={`px-3 py-2 rounded-xl text-sm font-medium border transition-all ${
                      selectedDate === d
                        ? 'bg-primary-600 text-white border-primary-600 shadow-sm'
                        : hasAvailable
                        ? 'bg-white text-gray-700 border-gray-200 hover:border-primary-300 hover:text-primary-700'
                        : 'bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed'
                    }`}
                  >
                    <div className="text-xs font-normal opacity-75">
                      {new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short' })}
                    </div>
                    <div>
                      {new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Step 2: Pick a time slot */}
          {selectedDate && (
            <div>
              <h2 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <span className="w-6 h-6 bg-primary-600 text-white rounded-full text-xs flex items-center justify-center font-bold">2</span>
                Choose a time on {fmtDateFull(selectedDate)}
              </h2>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {slotsForDate.map((slot) => (
                  <button
                    key={slot.id}
                    onClick={() => slot.available && setSelectedSlot(slot)}
                    disabled={!slot.available}
                    className={`py-3 px-2 rounded-xl text-sm font-medium border transition-all text-center ${
                      selectedSlot?.id === slot.id
                        ? 'bg-primary-600 text-white border-primary-600 shadow-sm'
                        : slot.available
                        ? 'bg-white text-gray-700 border-gray-200 hover:border-primary-300 hover:text-primary-700'
                        : 'bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed relative'
                    }`}
                  >
                    {fmtTime(slot.slot_time)}
                    {!slot.available && (
                      <div className="text-xs mt-0.5 text-gray-300">Unavailable</div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Confirm */}
          {selectedSlot && (
            <div>
              <h2 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <span className="w-6 h-6 bg-primary-600 text-white rounded-full text-xs flex items-center justify-center font-bold">3</span>
                Confirm your selection
              </h2>
              <div className="bg-primary-50 border border-primary-100 rounded-xl p-4 mb-4">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-2xl">📅</span>
                  <div>
                    <div className="font-semibold text-primary-900">
                      {fmtDateFull(selectedSlot.slot_date)}
                    </div>
                    <div className="text-primary-700 text-sm">{fmtTime(selectedSlot.slot_time)}</div>
                  </div>
                  {selectedSlot.location && (
                    <>
                      <span className="text-gray-300">·</span>
                      <div className="flex items-center gap-1 text-sm text-primary-700">
                        <span>📍</span> {selectedSlot.location}
                      </div>
                    </>
                  )}
                </div>
              </div>
              <button
                onClick={handleBook}
                disabled={confirming}
                className="w-full py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 disabled:opacity-60 transition-colors"
              >
                {confirming ? 'Booking…' : 'Confirm Interview'}
              </button>
              <p className="text-xs text-gray-400 mt-2 text-center">
                You'll receive a confirmation email immediately after booking.
              </p>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-gray-400 mt-4">
          This scheduling link is unique to your application.
        </p>
      </div>
    </div>
  );
}

// ── Formatting ────────────────────────────────────────────────────────────────

function fmtTime(t) {
  try {
    const [h, m] = t.split(':').map(Number);
    const period = h < 12 ? 'AM' : 'PM';
    const h12 = h % 12 || 12;
    return `${h12}:${String(m).padStart(2, '0')} ${period}`;
  } catch {
    return t;
  }
}

function fmtDateFull(dateStr) {
  try {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return dateStr;
  }
}
